"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from "react"

export type FloatingPanel = "currency" | "contact" | null

const DOCK_STORAGE_KEY = "theciu_floating_dock_visible"

type FloatingActionsContextValue = {
  openPanel: FloatingPanel
  setOpenPanel: (panel: FloatingPanel) => void
  togglePanel: (panel: Exclude<FloatingPanel, null>) => void
  dockVisible: boolean
  toggleDock: () => void
}

const FloatingActionsContext =
  createContext<FloatingActionsContextValue | null>(null)

export const useFloatingActions = () => {
  const ctx = useContext(FloatingActionsContext)
  if (!ctx) {
    throw new Error("useFloatingActions must be used within FloatingActionsProvider")
  }
  return ctx
}

export const FloatingActionsProvider = ({
  children
}: {
  children: ReactNode
}) => {
  const [openPanel, setOpenPanel] = useState<FloatingPanel>(null)
  const [dockVisible, setDockVisible] = useState(true)

  useEffect(() => {
    try {
      if (window.localStorage.getItem(DOCK_STORAGE_KEY) === "0") {
        setDockVisible(false)
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    if (!dockVisible) setOpenPanel(null)
  }, [dockVisible])

  const toggleDock = useCallback(() => {
    setDockVisible(current => {
      const next = !current
      try {
        window.localStorage.setItem(DOCK_STORAGE_KEY, next ? "1" : "0")
      } catch {
        // ignore
      }
      return next
    })
  }, [])

  const togglePanel = useCallback((panel: Exclude<FloatingPanel, null>) => {
    setOpenPanel(current => (current === panel ? null : panel))
  }, [])

  const value = useMemo(
    () => ({
      openPanel,
      setOpenPanel,
      togglePanel,
      dockVisible,
      toggleDock
    }),
    [openPanel, togglePanel, dockVisible, toggleDock]
  )

  return (
    <FloatingActionsContext.Provider value={value}>
      {children}
    </FloatingActionsContext.Provider>
  )
}
